import { useMemo, useRef } from 'react';
import { Button, IconButton, makeStyles, Tooltip, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { useDispatch, useSelector } from 'react-redux';
import {
  closeInfoPane, openNextPageFromHistory,
  openPreviousPageFromHistory,
} from '../../../redux_store';
import cx from 'classnames';
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import EditIcon from '@material-ui/icons/Edit';
import { calculateTruncatedText } from './helpers';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    position: 'relative',
  },
  header: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.secondary.main,
    opacity: 1,
    transition: '0.3s',
  },
  title: {
    fontSize: 32,
    fontWeight: 500,
    lineHeight: 1.2,
    opacity: 1,
    transition: '0.3s',
    color: 'black',
    margin: '8px 24px 0',
    display: 'inline',
    alignItems: 'end',
  },
  titleText: {
    display: 'inline',
    maxWidth: 300,
    maxHeight: 'calc(32px * 1.2 * 2)',
    margin: 0,
    fontSize: 32,
    lineHeight: 1.2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontWeight: 500,
  },
  titleTemplate: {
    position: 'relative',
    color: '#ffffff',
    width: 32,
    height: 30,
  },
  subTitle: {
    fontSize: 18,
    opacity: 1,
    transition: '0.3s',
    margin: '18px 24px',
  },
  content: {
    opacity: 1,
    transition: '0.3s',
  },
  headerInfoIcon: {
    color: theme.palette.secondary.main,
    width: 20,
    height: 20,
    transition: '0.3s',
  },
  headerInfoBigIcon: {
    width: 24,
    height: 24,
    transition: '0.3s',
    color: '#2E48D3',
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '24px 24px 0',
    transition: '0.3s',
  },
  headerContainerExpanded: {
    margin: 0,
    transition: '0.3s',
  },
  headerTextContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  headerButtonsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    margin: '14px 24px 24px',
  },
  hideInfo: {
    opacity: 0,
    transition: '0.3s',
  },
  buttonGroup: {
    marginRight: '-8px',
  },
  scrollableArea: {
    overflowY: 'auto',
    overflowX: 'clip',
    flex: '1 1 auto',
    marginBottom: 30,
  },
  editButtonContainer: {
    content: '',
    display: 'inline-block',
    verticalAlign: 'middle',
    marginBottom: 6,
    position: 'absolute',
    top: '50%',
    left: 0,
    transform: 'translate(0%, -50%)',
  },
  editButton: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 8,
  },
  editButtonIcon: {
    display: 'inline-block',
    padding: 0,
    minHeight: 0,
    minWidth: 0,
  },
}));

const BaseInfoWidget = ({
                          breadcrumbs,
                          title,
                          subtitle,
                          actions,
                          content,
                          editButton,
                        }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    isInfoPaneExpanded,
    currentPage: { indexInHistory },
    history,
  } = useSelector((state) => state.infoPane);

  const onClickDismiss = () => {
    dispatch(closeInfoPane());
  };

  const openPreviousPage = () => {
    dispatch(openPreviousPageFromHistory());
  };

  const openNexPage = () => {
    dispatch(openNextPageFromHistory());
  };

  // Simple cache to prevent repeated calculations
  const titleCacheRef = useRef(new Map());

  const memoTitle = useMemo(() => {
    if (!title) return '';

    // Check cache first
    if (titleCacheRef.current.has(title)) {
      return titleCacheRef.current.get(title);
    }

    // Calculate and cache result
    const result = calculateTruncatedText(title);
    titleCacheRef.current.set(title, result);

    // Limit cache size
    if (titleCacheRef.current.size > 50) {
      const firstKey = titleCacheRef.current.keys().next().value;
      titleCacheRef.current.delete(firstKey);
    }

    return result;
  }, [title]);

  return <div className={classes.container}>
    <div className={cx(classes.headerContainer, {
      [classes.headerContainerExpanded]: !isInfoPaneExpanded,
    })}>
      <div className={classes.headerTextContainer}>
        <InfoOutlinedIcon className={cx(classes.headerInfoIcon, {
          [classes.headerInfoBigIcon]: !isInfoPaneExpanded,
        })} />
        <Typography className={cx(classes.header, {
          [classes.hideInfo]: !isInfoPaneExpanded,
        })}>
          {breadcrumbs}
        </Typography>
      </div>

      <div className={cx(classes.buttonGroup, {
        [classes.hideInfo]: !isInfoPaneExpanded,
      })}>
        <IconButton onClick={openPreviousPage} size="small" disabled={indexInHistory < 1}>
          <KeyboardArrowLeftIcon />
        </IconButton>
        <IconButton onClick={openNexPage} size="small"
                    disabled={indexInHistory > history.length - 2}>
          <KeyboardArrowRightIcon />
        </IconButton>
        <IconButton onClick={onClickDismiss} size="small" style={{ width: 30, height: 30 }}>
          <CloseIcon style={{ width: 20, height: 20 }} />
        </IconButton>
      </div>
    </div>
    <div className={cx(classes.title, {
      [classes.hideInfo]: !isInfoPaneExpanded,
    })}>
      <span className={classes.titleText}>
        {memoTitle}
      </span>
      <span className={classes.titleTemplate}>{'..'}
        <div className={classes.editButtonContainer}>
        {!!editButton && <Tooltip
          title={editButton.hint}
        >
          <div className={classes.editButton}>
            <Button
              disabled={editButton.disabled}
              variant={'outlined'}
              color={'primary'}
              onClick={editButton.onClick}
              className={classes.editButtonIcon}
            >
              {<div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 22,
                height: 22,
              }}>
                <EditIcon style={{ width: 12, height: 12 }} />
              </div>}
            </Button>
          </div>
        </Tooltip>}
      </div>
      </span>
    </div>
    {
      actions && <div className={cx(classes.headerButtonsContainer, {
        [classes.hideInfo]: !isInfoPaneExpanded,
      })}>
        {
          (actions ?? []).map(({ onClick, tooltipTitle, buttonTitle, disabled }) => <Tooltip
            disableFocusListener={!tooltipTitle}
            disableHoverListener={!tooltipTitle}
            disableTouchListener={!tooltipTitle}
            title={tooltipTitle}
          >
            <div>
              <Button
                disabled={disabled}
                variant={'outlined'}
                color={'primary'}
                onClick={onClick}
              >
                {buttonTitle}
              </Button>
            </div>
          </Tooltip>)
        }
      </div>
    }

    <div className={classes.scrollableArea}>
      <Typography className={cx(classes.subTitle, {
        [classes.hideInfo]: !isInfoPaneExpanded,
      })}>
        {subtitle}
      </Typography>
      <div className={cx(classes.content, {
        [classes.hideInfo]: !isInfoPaneExpanded,
      })}>
        {content}
      </div>
    </div>
  </div>;
};

export default BaseInfoWidget;
